import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@ui/components/ui/breadcrumb";
import { type JSX, type ReactNode } from "react";
import { NavLink, type UIMatch } from "react-router";

import { useBreadcrumbsContext } from "src/contexts/BreadcrumbsContext";

import { type LoaderBreadcrumb } from "./createRouteHandle";
import { type LoaderOut } from "./remix";

function* interperse<T>(arr: T[], sep: (i: number) => T) {
  yield arr[0];

  for (let i = 1; i < arr.length; i++) {
    yield sep(i);
    yield arr[i];
  }
}

export function isObjectWithBreadcrumb(t: unknown): t is LoaderBreadcrumb {
  return (
    typeof t === "object" &&
    t !== null &&
    !Array.isArray(t) &&
    "breadcrumb" in t &&
    t.breadcrumb !== undefined
  );
}

export function isUIMatchWithHandle(
  t: UIMatch,
): t is UIMatch<
  Required<LoaderOut<unknown>>,
  { breadcrumb: (...args: any) => ReactNode }
> {
  return isObjectWithBreadcrumb(t.handle) && isObjectWithBreadcrumb(t.data);
}

export function createBreadcrumbs<T extends UIMatch>(uiMatches: T[]) {
  const uiMatchesWithHandle = uiMatches.filter((ui) => isUIMatchWithHandle(ui));
  const components: JSX.Element[] = [];
  const breadcrumbContext = useBreadcrumbsContext();

  for (let i = 0; i < uiMatchesWithHandle.length; i++) {
    const match = uiMatchesWithHandle[i];
    const override =
      match.data !== null && match.data.breadcrumb.id !== undefined
        ? breadcrumbContext.getOverrideById(match.data.breadcrumb.id)
        : undefined;
    const breadcrumb = match.handle.breadcrumb(match.data.breadcrumb, {
      override: override?.value,
    });

    let component;
    if (i === uiMatchesWithHandle.length - 1) {
      component = <BreadcrumbPage>{breadcrumb}</BreadcrumbPage>;
    } else {
      component = (
        <BreadcrumbLink asChild>
          <NavLink to={match.data.breadcrumb.href}>{breadcrumb}</NavLink>
        </BreadcrumbLink>
      );
    }
    components.push(<BreadcrumbItem key={i}>{component}</BreadcrumbItem>);
  }

  if (components.length === 0) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {Array.from(
          interperse(components, (index) => (
            <BreadcrumbSeparator key={`${index}-separator`} />
          )),
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
